import type { ReactNode } from 'react';
import { createRootRoute, HeadContent, lazyRouteComponent, Scripts } from '@tanstack/react-router';
import faviconUrl from '../favicon.ico?url';

const GOOGLE_MAPS_SRC = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_API_KEY}&loading=async&libraries=places,drawing,visualization,geometry,marker`;

function RootDocument({ children }: { children: ReactNode }) {
  return (
    // No `lang`: the UI language is a per-user setting the server does not know, and a wrong
    // value would make browsers offer to translate the page. initTheme() toggles the `dark`
    // class on <html> before hydration.
    // eslint-disable-next-line jsx-a11y/html-has-lang
    <html suppressHydrationWarning>
      <head>
        <HeadContent />
        <style>{`
          html {
            width: 100%;
            height: 100%;
            background-color: #282830;
          }
          #app {
            height: 100%;
            width: 100%;
            overflow-y: hidden;
          }
        `}</style>
      </head>
      <body>
        <div id="app">{children}</div>
        <div id="modal-root" />
        <div id="notification-root" />
        <div id="tlc-actions" />
        {/* Plain tags, not route `scripts`: those are re-inserted after hydration and the
            Maps API would run twice. Maps must stay before <Scripts /> (the app entry). */}
        <script src="https://www.google.com/recaptcha/api.js?render=explicit" async defer />
        <script src={GOOGLE_MAPS_SRC} />
        <Scripts />
      </body>
    </html>
  );
}

export const Route = createRootRoute({
  // The app reads window/localStorage while its modules load (config, services, stores), so
  // the server renders only the document above. Child routes inherit this and cannot opt back in.
  ssr: false,
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { title: 'CCC' },
      { name: 'theme-color', content: '#B50536' },
      { httpEquiv: 'X-UA-Compatible', content: 'IE=edge' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
    ],
    links: [
      { rel: 'icon', href: faviconUrl },
      { rel: 'stylesheet', href: 'https://fonts.cdnfonts.com/css/poppins' },
    ],
  }),
  shellComponent: RootDocument,
  // A dynamic import keeps the app's module graph out of the server render.
  component: lazyRouteComponent(() => import('../App')),
});
